/**
 * Conversation → Routing — step 7. Lets the SAME Routing Engine route Conversations, not
 * just notifications. This is the architectural payoff of "email is just a producer": a
 * conversation becomes a WorkItem and flows through the existing routeWork(), so the Work
 * Queue can reason about "the Conference that needs a decision" or "the solicitor email
 * awaiting a reply" without depending on whether a notification happened to fire (#4).
 *
 * Pure: purpose + conversation state → WorkItem traits → routeWork decides the disposition.
 */
import type { WorkItem, WorkSource } from './routingEngine';
import type { MatterConversation, ConversationPurpose, CommunicationChannel } from './conversationsApi';

// Purpose → its baseline work traits (before the inbound-reply override below).
type Traits = Pick<WorkItem, 'isInstruction' | 'needsReply' | 'hasDeadline' | 'isBillable' | 'isDocument' | 'isDecision' | 'isActionable'>;

const PURPOSE_TRAITS: Record<ConversationPurpose, Traits> = {
  conference:          { isDecision: true },     // → needs_decision (approve a slot / decline)
  billing:             { isBillable: true },      // → needs_money
  disclosure:          { isDocument: true },      // → needs_document_review
  instruction:         { isInstruction: true },   // → needs_new_matter (no matter) / file on matter
  appeal:              { isInstruction: true },   // → an appeal opens a (new) matter
  hearing_preparation: { isActionable: true },    // → to do
  court_operations:    { hasDeadline: true },     // → needs_diary (a court change to diarise/act on)
  judgment:            { isActionable: true },    // → to do
  settlement:          { isActionable: true },    // → to do (unless someone is waiting — see below)
  general_advice:      { isActionable: true },
  administration:      { isActionable: true },
  general:             {},                         // pure inbox chatter — only actionable if someone waits
};

const CHANNEL_SOURCE: Record<CommunicationChannel, WorkSource> = {
  email: 'email', portal: 'portal', in_app: 'clerk', conference: 'conference',
  note: 'ai', ai_draft: 'ai', sms: 'mobile', whatsapp: 'mobile',
};

/**
 * Normalise a Conversation into a WorkItem. A closed conversation needs nothing; an open
 * one takes its purpose's traits, and if the LAST message is inbound (a solicitor/clerk is
 * waiting on us) the reply obligation takes precedence over passive purposes.
 */
export function conversationToWorkItem(c: MatterConversation): WorkItem {
  const last = c.messages[c.messages.length - 1];
  const inboundWaiting = last?.direction === 'inbound';

  const base: Traits = c.status === 'closed' ? {} : { ...PURPOSE_TRAITS[c.purpose] };

  // Someone is waiting on a reply — this outranks the passive purposes, but never
  // overrides a hard obligation (a conference decision, a document to review, money).
  const passive = !base.isDecision && !base.isDocument && !base.isBillable && !base.isInstruction;
  if (c.status !== 'closed' && inboundWaiting && passive) {
    base.needsReply = true;
    base.isActionable = undefined;
  }

  return {
    id: c.id,
    source: last ? CHANNEL_SOURCE[last.channel] : 'system',
    kind: `conversation_${c.purpose}`,
    title: c.purpose,
    matterId: c.matterId,
    ...base,
  };
}
